/**
 * Futures Position dashboard — `/dashboard/futures-position`.
 *
 * Convert 1:1 từ PBI page "Futures Postion" (`PBI_ERP.pbix`): HEADER (title +
 * report_date + date range slicer) + GRID 2×2 bốn bảng:
 *   1. London - Robusta   3. Futures Position
 *   2. Newyork - Arabica   4. Total lot (ma trận flag × BUY/SELL, cột động, scroll ngang)
 *
 * DATA: đọc từ Report DB qua `useGetFuturesPositionDashboardQuery`
 * (GET /api/v1/dashboard/futures-position); date range persist per-user qua
 * `usePreference` ('dashboard'/'page_filter', sub_key 'futures_position'). Tái dùng
 * `PnLTable` + tokens của dashboard PnL (đồng nhất style tài chính: header navy,
 * số âm ngoặc + đỏ, zebra, hàng Total đậm).
 */
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Button, CircularProgress, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { usePreference } from '../../../hooks/usePreference'
import { useGetFuturesPositionDashboardQuery } from '../../../store/api/dashboardFuturesPositionApi'
import { ReportPageToolbar } from '../../report/reportToolbarSlot'
import { useReportZoom } from '../_shared/useReportZoom'
import { FILTER_HEADER_INPUT_SX } from '../../../theme/responsive'
import PnLTable, { type PnLRow } from '../pnl/PnLTable'
import {
  buildTotalLotTable,
  flattenTotalLotRows,
  futuresPosition,
  londonRobusta,
  newyorkArabica,
  totalLot,
  type FpTable,
} from './mockData'

/** Card trắng + tiêu đề xanh đậm (đồng bộ PnL). */
function FpCard({
  title,
  unit,
  data,
  fill,
  loading,
}: {
  title: string
  unit?: string
  data: FpTable
  /** Card hàng dưới: fill hết phần màn hình còn lại (desktop), bảng scroll
   *  nội bộ + sticky header. Mobile (1 cột, page scroll) → khoá 460px. */
  fill?: boolean
  /** Reload theo từng block: progress bar + mờ bảng RIÊNG card này khi refetch. */
  loading?: boolean
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        border: '1px solid #dfe3e8',
        borderRadius: '8px',
        bgcolor: '#fff',
        minWidth: 0,
        ...(fill ? { display: 'flex', flexDirection: 'column', minHeight: 0 } : {}),
      }}
    >
      {/* Card chrome đồng bộ Cashflows: title ink 0.78rem/700, caption gold 0.66rem/600. */}
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.75, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a1a', letterSpacing: '0.01em' }}>{title}</Typography>
        <Box sx={{ flex: 1 }} />
        {unit && (
          <Typography sx={{ fontSize: '0.66rem', color: '#b28a3b', fontWeight: 600 }}>{unit}</Typography>
        )}
      </Stack>
      <Box sx={{ height: 2, mb: 0.25, flexShrink: 0 }}>{loading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}</Box>
      <Box
        sx={{
          opacity: loading ? 0.55 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          transition: 'opacity 0.15s',
          ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
        }}
      >
        <PnLTable
          columns={data.columns}
          rows={data.rows}
          headerGroups={data.headerGroups}
          height={fill ? { xs: 460, lg: '100%' } : undefined}
        />
      </Box>
    </Paper>
  )
}

/** Persist date range per-user cross-session (user_preferences registry). */
interface DashboardFuturesPositionPageFilter {
  dateFrom: string | null
  dateTo: string | null
}

export default function FuturesPositionPage() {
  // Date range — persist qua user_preferences ('dashboard'/'page_filter', sub_key 'futures_position').
  const filter = usePreference<DashboardFuturesPositionPageFilter>(
    'dashboard',
    'page_filter',
    'futures_position',
  )
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)
  const hydrated = useRef(false)

  // Hydrate state 1 lần từ preference (default ⊕ user delta).
  useEffect(() => {
    if (filter.isLoading || hydrated.current) return
    hydrated.current = true
    setFromDate(filter.value.dateFrom ? dayjs(filter.value.dateFrom) : null)
    setToDate(filter.value.dateTo ? dayjs(filter.value.dateTo) : null)
  }, [filter])

  // Debounced save khi user đổi date. KHÔNG cần invalidateTags: 2 date là query
  // arg của useGetFuturesPositionDashboardQuery → args đổi tự refetch.
  useEffect(() => {
    if (!hydrated.current) return
    const timer = setTimeout(() => {
      filter
        .patch({
          dateFrom: fromDate ? fromDate.format('YYYY-MM-DD') : null,
          dateTo: toDate ? toDate.format('YYYY-MM-DD') : null,
        })
        .catch(() => undefined)
    }, 450)
    return () => clearTimeout(timer)
  }, [fromDate, toDate, filter])

  // Data từ Report DB. Override `rows` của config mock bằng rows API khi có.
  const { data, isFetching, refetch } = useGetFuturesPositionDashboardQuery(
    {
      fromDate: fromDate?.format('YYYY-MM-DD'),
      toDate: toDate?.format('YYYY-MM-DD'),
    },
    { refetchOnMountOrArgChange: true },
  )
  const { zoom, control } = useReportZoom()

  // Ép refetch MỖI lần user đổi date — kể cả chọn lại đúng giá trị đang hiển thị
  // (args không đổi ⇒ RTK cache hit, không tự gọi).
  const [reloadNonce, setReloadNonce] = useState(0)
  useEffect(() => {
    if (!hydrated.current || reloadNonce === 0) return
    refetch()
  }, [reloadNonce, refetch])
  const pickFrom = (d: Dayjs | null) => {
    setFromDate(d)
    setReloadNonce((n) => n + 1)
  }
  const pickTo = (d: Dayjs | null) => {
    setToDate(d)
    setReloadNonce((n) => n + 1)
  }

  const withRows = (config: FpTable, rows?: PnLRow[]): FpTable =>
    rows && rows.length > 0 ? { ...config, rows } : config

  // Total lot: cột ĐỘNG theo flags từ API (PBI pivot flag × BUY/SELL).
  const totalLotData = useMemo(() => {
    if (!data?.totalLot || data.totalLot.rows.length === 0) return totalLot
    return buildTotalLotTable(data.totalLot.flags, flattenTotalLotRows(data.totalLot.rows))
  }, [data])

  // PBI card `report_date` — fallback hôm nay khi chưa có payload.
  const reportDate = data?.reportDate ? dayjs(data.reportDate) : dayjs()

  return (
    // Desktop: khoá page = chiều cao `<main>` (app-shell scroll) → hàng bảng dưới
    // fill hết màn hình, scroll nội bộ — không còn khoảng trống. Mobile giữ page scroll.
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        pb: { xs: 2, lg: 0 },
        height: { lg: '100%' },
        minHeight: 0,
      }}
    >
      {/* ── HEADER: title + report_date + date range ── (portal NGUYÊN block lên ReportsPage) */}
      <ReportPageToolbar>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 1,
          border: '1px solid #dfe3e8',
          borderRadius: '8px',
          bgcolor: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Date nằm KẾ BÊN title (1 dòng) để header thấp. Title forest + date gold (skin Cashflows). */}
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, fontWeight: 900, color: '#0b3633', letterSpacing: '0.01em', lineHeight: 1.15 }}>
            FUTURES POSITION
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#b28a3b', fontWeight: 600 }}>
            {reportDate.format('MMM DD, YYYY')}
          </Typography>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Format filter copy từ PnL (spacing 0.45 + FILTER_HEADER_INPUT_SX +
              label picker đầu + icon `→`). */}
          <Stack direction="row" spacing={0.45} alignItems="center" flexWrap="wrap" useFlexGap>
            <DatePicker
              label="Period"
              format="DD-MMM-YYYY"
              value={fromDate}
              onChange={pickFrom}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: FILTER_HEADER_INPUT_SX,
                  InputLabelProps: { shrink: true },
                  inputProps: { 'aria-label': 'Period start date' },
                },
              }}
            />
            <ArrowRightAltIcon
              fontSize="small"
              sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
              aria-hidden="true"
            />
            <DatePicker
              format="DD-MMM-YYYY"
              value={toDate}
              onChange={pickTo}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: FILTER_HEADER_INPUT_SX,
                  inputProps: { 'aria-label': 'Period end date' },
                },
              }}
            />
          </Stack>
        </LocalizationProvider>
        {control}
        {/* Reload toàn dashboard — refetch() ép gọi lại kể cả khi args không đổi
            (style nút theo BatchReportPage). */}
        <Tooltip title="Reload dashboard data">
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem' }}
            >
              Reload
            </Button>
          </span>
        </Tooltip>
      </Paper>
      </ReportPageToolbar>

      {/* ── GRID 2×2 — desktop 2 cột, hàng trên auto + hàng dưới fill phần còn
          lại của màn hình (bảng dài scroll nội bộ); mobile stack 1 cột. ── */}
      <Box
        sx={{
          zoom,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr)' },
          gridTemplateRows: { lg: 'auto minmax(0, 1fr)' },
          gap: 1.5,
          alignItems: { xs: 'start', lg: 'stretch' },
          flex: { lg: '1 1 auto' },
          minHeight: 0,
        }}
      >
        <FpCard title="London - Robusta" data={withRows(londonRobusta, data?.londonRobusta)} loading={isFetching} />
        <FpCard title="Newyork - Arabica" data={withRows(newyorkArabica, data?.newyorkArabica)} loading={isFetching} />
        <FpCard
          title="Futures Position"
          data={withRows(futuresPosition, data?.futuresPosition)}
          fill
          loading={isFetching}
        />
        <FpCard title="Total lot" data={totalLotData} fill loading={isFetching} />
      </Box>
    </Box>
  )
}
